/** Browser/server validation of optional, geometry-only saved layouts. */
export const MAX_LAYOUT_CHECKPOINTS = 30;
export const MAX_LAYOUT_NAME = 120;
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const identity = (value: unknown) => typeof value === "string" && uuid.test(value);
const keys = (value: Record<string, unknown>, allowed: string[]) =>
  Object.keys(value).every((key) => allowed.includes(key));
const point = (value: unknown) => record(value) && keys(value, ["x", "y"]) &&
  typeof value.x === "number" && Number.isFinite(value.x) &&
  typeof value.y === "number" && Number.isFinite(value.y);
const dimension = (value: unknown) => value === undefined ||
  (typeof value === "number" && Number.isFinite(value) && value > 0);
const fraction = (value: unknown) => value === undefined ||
  (typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1);
const slot = (value: unknown) => value === undefined ||
  (typeof value === "number" && Number.isSafeInteger(value) && value >= 0 && value <= 100000);

/** Historical IDs deliberately do not require references in the current document. */
export function validLayoutCheckpoints(value: unknown): boolean {
  if (value === undefined) return true;
  if (!Array.isArray(value) || value.length > MAX_LAYOUT_CHECKPOINTS) return false;
  const checkpointIds = new Set<string>(), names = new Set<string>();
  for (const checkpoint of value) {
    if (!record(checkpoint) || !keys(checkpoint, ["checkpointId", "name", "createdAt", "positions", "connectors"]) ||
        !identity(checkpoint.checkpointId) || checkpointIds.has(checkpoint.checkpointId as string) ||
        typeof checkpoint.name !== "string" || !checkpoint.name.trim() || checkpoint.name.length > MAX_LAYOUT_NAME ||
        typeof checkpoint.createdAt !== "string" || checkpoint.createdAt.length > 40 || !Number.isFinite(Date.parse(checkpoint.createdAt)) ||
        !Array.isArray(checkpoint.positions) || checkpoint.positions.length > 10000 ||
        !Array.isArray(checkpoint.connectors) || checkpoint.connectors.length > 20000) return false;
    const name = checkpoint.name.trim().normalize("NFKC").toLowerCase();
    if (names.has(name)) return false;
    names.add(name); checkpointIds.add(checkpoint.checkpointId as string);
    const appearanceIds = new Set<string>(), objectIds = new Set<string>(), relationshipIds = new Set<string>();
    for (const position of checkpoint.positions) {
      if (!record(position) || !keys(position, ["appearanceId", "objectId", "position", "width", "height"]) ||
          !identity(position.appearanceId) || !identity(position.objectId) || !point(position.position) ||
          !dimension(position.width) || !dimension(position.height) ||
          appearanceIds.has(position.appearanceId as string) || objectIds.has(position.objectId as string)) return false;
      appearanceIds.add(position.appearanceId as string); objectIds.add(position.objectId as string);
    }
    for (const connector of checkpoint.connectors) {
      if (!record(connector) || !keys(connector, ["relationshipId", "sourceSide", "targetSide", "sourceSlot", "targetSlot", "sourceFraction", "targetFraction", "waypoints", "routing", "sourceObjectId", "targetObjectId"]) ||
          !identity(connector.relationshipId) || relationshipIds.has(connector.relationshipId as string) ||
          [connector.sourceObjectId, connector.targetObjectId].some((id) => id !== undefined && !identity(id)) ||
          [connector.sourceSide, connector.targetSide].some((side) => side !== undefined && !["top", "right", "bottom", "left"].includes(side as string)) ||
          !slot(connector.sourceSlot) || !slot(connector.targetSlot) ||
          !fraction(connector.sourceFraction) || !fraction(connector.targetFraction) ||
          (connector.routing !== undefined && !["auto", "manual"].includes(connector.routing as string)) ||
          (connector.waypoints !== undefined && (!Array.isArray(connector.waypoints) || connector.waypoints.length > 1000 || !connector.waypoints.every(point)))) return false;
      relationshipIds.add(connector.relationshipId as string);
    }
  }
  return true;
}
